// src/components/validationNotes/NotesEnseignantSuivi.jsx
// ============================================================================
// Suivi des enseignants - onglet « Notes » de la page d'un enseignant
// ============================================================================
//
// Les saisies de notes de cet enseignant cette année, chacune menant à SA
// page, la même que depuis le carnet : une seule page de validation, deux
// entrées (décision de Daniel du 7 octobre 2026) :
//   - collège et lycée : les feuilles, rangées par classe et matière, vers la
//     page de la feuille (/administration/notes/saisies/:id) ;
//   - primaire (N3) : les mois de ses classes, rangés par classe, vers la page
//     du mois (/administration/notes/saisies/primaire/:classeId/:moisId), où la
//     direction valide le mois en un geste et ouvre la feuille d'une matière.
//
// Cet onglet ne décide rien : il dit où en est chaque saisie. Les boutons
// sont sur la page de la feuille ou du mois, selon les droits de la personne
// - une école qui n'a pas la licence du suivi doit pouvoir valider quand
// même, depuis le carnet.
//
// Il lit /api/validation-notes (filtre `enseignant_id` : les feuilles qu'il a
// ouvertes, modifiées ou soumises, et les mois de ses classes du primaire),
// pas la fiche du suivi : la page de l'enseignant n'affiche l'onglet que si la
// personne lit au moins un niveau (NOTES.VALIDATION.* ou
// NOTES.VALIDATION_PRIMAIRE.*) et que les deux licences sont là
// (useAccesValidationNotes). Le serveur ne rend que les niveaux qu'elle lit.
// ============================================================================

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronRight, Inbox, Loader2 } from 'lucide-react';
import EtatVide from '../ui/EtatVide';
import { usePermissions } from '../../hooks/usePermissions';
import { useFeatures } from '../../contexts/FeaturesContext';
import { dateHeure, libelleDe, nombre, pluriel } from '../suiviEnseignants/formatSuivi';
import { validationNotes } from './apiValidationNotes';
import {
  BadgeEtat, BadgeStatutFeuille, PaginationFeuilles, adresseMois, estPrimaire, etatDuMois, intituleFeuille,
  libelleTrimestre, periodeFeuille, resumeDuMois
} from './presentationNotes';

// Toutes les feuilles d'un enseignant tiennent sur une page : trois
// trimestres × trois épreuves par classe-matière. Le serveur en accepte 200
// (`par_page`, fileEcole) ; à 50, un groupe classe × matière était coupé entre
// deux pages, et la vue « par classe et matière » n'en était plus une.
const PAR_PAGE_SUIVI = 200;

const LIGNE = 'flex flex-col gap-1.5 px-4 py-3 transition hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#246990]/30 sm:flex-row sm:items-center sm:gap-4';

/** La date qui compte pour la ligne, et ce qui reste à régler. */
function precision(f) {
  const conflits = nombre(f.conflits);
  return [
    f.statut === 'validee' && f.validee_le ? `Validée le ${dateHeure(f.validee_le)}` : '',
    f.statut === 'soumise' && f.soumise_le ? `Soumise le ${dateHeure(f.soumise_le)}` : '',
    f.statut === 'brouillon' ? 'Pas encore soumise' : '',
    f.statut === 'validee' && conflits > 0 ? `${pluriel(conflits, 'élève non reporté', 'élèves non reportés')} au carnet` : ''
  ].filter(Boolean).join(' · ');
}

const NotesEnseignantSuivi = ({ employeId }) => {
  const { hasPermission } = usePermissions();
  const { estActive } = useFeatures();
  const [donnees, setDonnees] = useState(null);
  const [page, setPage] = useState(1);
  const [chargement, setChargement] = useState(false);
  const [erreur, setErreur] = useState(null);
  const demande = useRef(0);

  const charger = useCallback(async () => {
    const numero = ++demande.current;
    setChargement(true);
    setErreur(null);
    try {
      const reponse = await validationNotes.liste({
        statut: 'tous', enseignant_id: employeId, par_page: PAR_PAGE_SUIVI, page: page > 1 ? page : ''
      });
      if (numero === demande.current) setDonnees(reponse.data);
    } catch (e) {
      if (numero === demande.current) setErreur(e.message);
    } finally {
      if (numero === demande.current) setChargement(false);
    }
  }, [employeId, page]);

  // Un autre enseignant : on repart de zéro, sans montrer un instant le précédent.
  useEffect(() => {
    setDonnees(null);
    setPage(1);
  }, [employeId]);
  useEffect(() => { charger(); }, [charger]);

  // Le primaire regroupé par mois (serveur N3) : ses feuilles ne sont pas
  // reprises une à une, elles sont dans la ligne de leur mois.
  const primaireRegroupe = Array.isArray(donnees?.mois_primaire);

  // Les mois du primaire, par classe, dans l'ordre du serveur.
  const classesPrimaire = useMemo(() => {
    const mois = primaireRegroupe ? donnees.mois_primaire : [];
    const parClasse = new Map();
    mois.forEach((m) => {
      const cle = String(m.classe_id);
      if (!parClasse.has(cle)) parClasse.set(cle, { cle, intitule: libelleDe(m.classe) || `Classe ${m.classe_id}`, mois: [] });
      parClasse.get(cle).mois.push(m);
    });
    return [...parClasse.values()].sort((a, b) => a.intitule.localeCompare(b.intitule, 'fr'));
  }, [donnees, primaireRegroupe]);

  // Par classe et matière (l'ordre alphabétique), puis trimestre et épreuve :
  // l'ordre du carnet. Le serveur trie par urgence ; ici on lit un parcours.
  const groupes = useMemo(() => {
    const toutes = Array.isArray(donnees?.feuilles) ? donnees.feuilles : [];
    const feuilles = primaireRegroupe ? toutes.filter((f) => !estPrimaire(f)) : toutes;
    const parCouple = new Map();
    feuilles.forEach((f) => {
      const cle = `${f.classe_id}|${f.matiere_id}`;
      if (!parCouple.has(cle)) parCouple.set(cle, { cle, intitule: intituleFeuille(f), feuilles: [] });
      parCouple.get(cle).feuilles.push(f);
    });
    const tries = [...parCouple.values()].sort((a, b) => a.intitule.localeCompare(b.intitule, 'fr'));
    tries.forEach((g) => g.feuilles.sort((a, b) => (
      String(a.trimestre).localeCompare(String(b.trimestre)) || Number(a.epreuve) - Number(b.epreuve)
    )));
    return tries;
  }, [donnees, primaireRegroupe]);

  if (!donnees) {
    return erreur ? (
      <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span className="min-w-0 flex-1">{erreur}</span>
        <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
      </div>
    ) : (
      <div role="status" className="flex items-center justify-center gap-2 py-12 text-sm text-gray-500">
        <Loader2 className="h-4 w-4 animate-spin text-[#246990]" />
        Chargement des saisies de notes…
      </div>
    );
  }

  const total = nombre(donnees.total);
  const pages = Math.max(1, nombre(donnees.pages));
  const compteurs = donnees.compteurs || {};
  const aTraiter = nombre(compteurs.a_traiter);
  const sansAnnee = !donnees.annee;
  const nbFeuillesListees = groupes.reduce((n, g) => n + g.feuilles.length, 0);
  const vide = classesPrimaire.length === 0 && nbFeuillesListees === 0;
  // La file du carnet, filtrée sur cet enseignant, pour qui y a accès.
  const carnetOuvert = hasPermission('CARNET_EVALUATION') && estActive('module.evaluations');

  return (
    <div className="space-y-3">
      {erreur && (
        <div role="alert" className="flex flex-wrap items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">Les saisies n'ont pas pu être rafraîchies : {erreur}</span>
          <button type="button" onClick={charger} className="font-semibold underline">Réessayer</button>
        </div>
      )}

      {sansAnnee ? (
        <EtatVide
          compact
          icone={Inbox}
          titre="Aucune année scolaire ouverte"
          description="Les enseignants saisissent leurs notes sur l'année active, non clôturée : il n'y en a pas en ce moment."
        />
      ) : vide ? (
        <EtatVide
          compact
          icone={Inbox}
          titre="Aucune saisie de notes"
          description="Cet enseignant n'a encore saisi aucune note depuis son espace cette année."
        />
      ) : (
        <>
          <p className="text-xs text-gray-600">
            {[
              classesPrimaire.length > 0 ? pluriel(classesPrimaire.reduce((n, c) => n + c.mois.length, 0), 'mois de classe', 'mois de classe') : '',
              nbFeuillesListees > 0 ? pluriel(primaireRegroupe ? nbFeuillesListees : total, 'feuille', 'feuilles') : ''
            ].filter(Boolean).join(' et ')} cette année
            {aTraiter > 0 && <span className="font-semibold text-[#246990]"> · {aTraiter} à traiter par la direction</span>}
          </p>

          {/* Primaire : les mois de ses classes */}
          {classesPrimaire.map((c) => (
            <section key={`primaire-${c.cle}`} className="rounded-lg border border-gray-200">
              <h3 className="border-b border-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-900">
                {c.intitule} <span className="font-normal text-gray-500">· Primaire, par mois</span>
              </h3>
              <ul className="divide-y divide-gray-100">
                {c.mois.map((m) => {
                  // Les comptes ne portent que sur SES feuilles (`enseignant_id`) :
                  // ni « à saisir » ni le total de la classe ne valent pour lui.
                  const etat = etatDuMois(m, { partiel: true });
                  return (
                    <li key={`${m.classe_id}-${m.mois_id}`}>
                      <Link
                        to={adresseMois(m.classe_id, m.mois_id)}
                        state={{ depuis: 'suivi', employeId }}
                        className={LIGNE}
                      >
                        <p className="text-sm text-gray-800 sm:w-[300px] sm:shrink-0">
                          {[libelleDe(m.mois) || 'Mois', m.trimestre ? libelleTrimestre(m.trimestre) : ''].filter(Boolean).join(' · ')}
                        </p>
                        <p className="min-w-0 flex-1 text-[11px] text-gray-500">
                          {resumeDuMois(m, { partiel: true }) || 'aucune saisie'}
                          {nombre(m.total_matieres) > 0 && ` · ${pluriel(nombre(m.total_matieres), 'matière')} dans la classe`}
                        </p>
                        <BadgeEtat ton={etat.ton} largeur={176}>{etat.libelle}</BadgeEtat>
                        <ChevronRight aria-hidden="true" className="hidden h-4 w-4 shrink-0 text-gray-400 sm:block" />
                      </Link>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {/* Collège et lycée : les feuilles, par classe et matière */}
          {groupes.map((g) => (
            <section key={g.cle} className="rounded-lg border border-gray-200">
              <h3 className="border-b border-gray-100 px-4 py-2.5 text-sm font-semibold text-gray-900">{g.intitule}</h3>
              <ul className="divide-y divide-gray-100">
                {g.feuilles.map((f) => (
                  <li key={f.id}>
                    <Link
                      to={`/administration/notes/saisies/${f.id}`}
                      state={{ depuis: 'suivi', employeId }}
                      className={LIGNE}
                    >
                      <p className="text-sm text-gray-800 sm:w-[300px] sm:shrink-0">{periodeFeuille(f)}</p>
                      <p className="min-w-0 flex-1 text-[11px] text-gray-500">{precision(f)}</p>
                      <BadgeStatutFeuille statut={f.statut} />
                      <ChevronRight aria-hidden="true" className="hidden h-4 w-4 shrink-0 text-gray-400 sm:block" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
          {pages > 1 && (
            <PaginationFeuilles
              page={nombre(donnees.page) || page}
              pages={pages}
              total={total}
              parPage={nombre(donnees.par_page) || PAR_PAGE_SUIVI}
              onChangerPage={setPage}
              desactive={chargement}
            />
          )}
        </>
      )}

      <p className="rounded-lg border border-[#246990]/20 bg-[#eef7fb] px-3 py-2.5 text-xs leading-relaxed text-gray-700">
        La validation se fait sur la page de chaque feuille - ou, au primaire, sur la page du mois de la classe -, la même
        que depuis le carnet d'évaluations : on y compare la saisie de l'enseignant avec le carnet avant de décider.
        {carnetOuvert && (
          <>
            {' '}
            <Link
              to={`/administration/notes?onglet=saisies&statut=tous&enseignant=${encodeURIComponent(employeId)}`}
              className="font-semibold text-[#246990] hover:underline"
            >
              Ouvrir ses saisies dans le carnet
            </Link>
          </>
        )}
      </p>
    </div>
  );
};

export default NotesEnseignantSuivi;
